import React, { useState, useEffect } from 'react';
import { Send, Mail, MessageSquare, User, AlertCircle, CheckCircle } from 'lucide-react';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', null
  const [validationErrors, setValidationErrors] = useState({});
  const [showRecaptchaModal, setShowRecaptchaModal] = useState(false);

  // reCAPTCHA widget ID
  let contactRecaptchaId;

  // Load reCAPTCHA script
  useEffect(() => {
    if (!window.grecaptcha) {
      const script = document.createElement('script');
      script.src = 'https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    // Define the global reCAPTCHA load function if not already defined
    if (!window.onRecaptchaLoad) {
      window.onRecaptchaLoad = () => {
      };
    }
  }, []);

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (!formData.message.trim()) {
      errors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      errors.message = 'Message must be at least 10 characters long';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Contact reCAPTCHA completion handler
  const onContactRecaptchaComplete = (response) => {
    
    // Submit the form with the reCAPTCHA response
    submitContactForm(response);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Check if reCAPTCHA is loaded
    if (typeof window.grecaptcha === 'undefined') {
      alert('reCAPTCHA is still loading. Please wait a moment and try again.');
      return;
    }

    
    // Show the reCAPTCHA modal
    setShowRecaptchaModal(true);
    
    // Wait for modal to be visible, then render reCAPTCHA
    setTimeout(() => {
      const recaptchaContainer = document.getElementById('contact-recaptcha');
      if (recaptchaContainer && window.grecaptcha) {
        recaptchaContainer.style.display = 'block';
        
        // Use grecaptcha.ready() to ensure it's loaded
        window.grecaptcha.ready(function() {
          try {
            if (typeof contactRecaptchaId === 'undefined') {
              contactRecaptchaId = window.grecaptcha.render('contact-recaptcha', {
                'sitekey': '6LcJXoYrAAAAAIOUMcQdyFATrydITIGns9PJ0twC',
                'callback': onContactRecaptchaComplete
              });
            } else {
              window.grecaptcha.reset(contactRecaptchaId);
            }
          } catch (e) {
            console.error('Error with contact reCAPTCHA:', e);
            alert('Could not load reCAPTCHA verification. Please try again later.');
            setShowRecaptchaModal(false);
          }
        });
      } else {
        console.error("reCAPTCHA container not found or grecaptcha not loaded!");
        alert('Could not open verification form. Please try again later.');
        setShowRecaptchaModal(false);
      }
    }, 100);
  };

  const submitContactForm = async (recaptchaResponse) => {
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Create data string
      let data = 'form_type=contact&name=' + encodeURIComponent(formData.name.trim()) +
                '&email=' + encodeURIComponent(formData.email.trim()) +
                '&message=' + encodeURIComponent(formData.message.trim());
      
      // Add reCAPTCHA response
      if (recaptchaResponse) {
        data += '&g-recaptcha-response=' + encodeURIComponent(recaptchaResponse);
      } else {
        // Get the response from grecaptcha
        const recaptchaResponseFromWidget = window.grecaptcha.getResponse(contactRecaptchaId);
        if (recaptchaResponseFromWidget) {
          data += '&g-recaptcha-response=' + encodeURIComponent(recaptchaResponseFromWidget);
        } else {
          console.error("Missing reCAPTCHA response");
          alert("Please complete the reCAPTCHA verification.");
          setIsSubmitting(false);
          return;
        }
      }

      // Send AJAX request
      const xhr = new XMLHttpRequest();
      xhr.open('POST', 'https://mgimpacts.com/send_mail.php', true);
      xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
      
      xhr.onload = function() {
        
        // Close reCAPTCHA modal
        setShowRecaptchaModal(false);
        
        if (xhr.status === 200) {
          try {
            const response = JSON.parse(xhr.responseText);
            
            if (response.success) {
              setSubmitStatus('success');
              setFormData({ name: '', email: '', message: '' });
              
              // Reset reCAPTCHA if it exists
              if (typeof contactRecaptchaId !== 'undefined' && window.grecaptcha) {
                window.grecaptcha.reset(contactRecaptchaId);
              }
            } else {
              setSubmitStatus('error');
            }
          } catch (e) {
            console.error('Error parsing contact response:', e);
            setSubmitStatus('error');
          }
        } else {
          setSubmitStatus('error');
        }
        
        setIsSubmitting(false);
      };
      
      xhr.onerror = function() {
        console.error("Contact form network error occurred");
        setSubmitStatus('error');
        setIsSubmitting(false);
        setShowRecaptchaModal(false);
      };
      
      xhr.send(data);

    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus('error');
      setIsSubmitting(false);
      setShowRecaptchaModal(false);
    }
  };

  const closeRecaptchaModal = () => {
    setShowRecaptchaModal(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0c1526] to-[#142039] text-white">
      {/* reCAPTCHA Modal */}
      {showRecaptchaModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white text-gray-800 p-6 rounded-lg shadow-lg max-w-sm w-full mx-4">
            <h2 className="text-lg font-semibold mb-2">Verify You're Human</h2>
            <p className="mb-4 text-sm text-gray-600">Please complete the verification below to send your message.</p>
            <div className="flex justify-center mb-4">
              <div id="contact-recaptcha" style={{ display: 'none' }}></div>
            </div>
            <div className="flex justify-center">
              <button 
                onClick={closeRecaptchaModal} 
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Get in Touch
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed">
              Having issues or need support? We're here to help! Send us a message and our team will get back to you as soon as possible.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div className="space-y-8">
              <div className="bg-[#1e293b] rounded-xl shadow-xl p-8 border border-gray-700">
                <h2 className="text-2xl font-bold text-white mb-6">
                  Contact Information
                </h2>
                
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-[#ff7e00] to-[#e11d48] rounded-lg flex items-center justify-center">
                      <Mail className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white">Email Support</h3>
                      <p className="text-gray-300">support@mgimpacts.com</p>
                      <p className="text-sm text-gray-400 mt-1">
                        We typically respond within 24 hours
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-[#ff7e00] to-[#e11d48] rounded-lg flex items-center justify-center">
                      <MessageSquare className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white">Quick Response</h3>
                      <p className="text-gray-300">Use the contact form for faster support</p>
                      <p className="text-sm text-gray-400 mt-1">
                        Get prioritized assistance for technical issues
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Common Issues */}
              <div className="bg-[#1e293b] rounded-xl shadow-xl p-8 border border-gray-700">
                <h2 className="text-2xl font-bold text-white mb-6">
                  Common Issues
                </h2>
                
                <div className="space-y-4">
                  <div className="p-4 bg-[#273349] rounded-lg">
                    <h3 className="font-semibold text-white mb-2">Account & Subscription</h3>
                    <p className="text-gray-300 text-sm">
                      Issues with your account, billing, or subscription management
                    </p>
                  </div>
                  
                  <div className="p-4 bg-[#273349] rounded-lg">
                    <h3 className="font-semibold text-white mb-2">Technical Problems</h3>
                    <p className="text-gray-300 text-sm">
                      Battlecard generation errors, app bugs, or performance issues
                    </p>
                  </div>
                  
                  <div className="p-4 bg-[#273349] rounded-lg">
                    <h3 className="font-semibold text-white mb-2">Feature Requests</h3>
                    <p className="text-gray-300 text-sm">
                      Suggestions for new features or improvements to existing ones
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-[#1e293b] rounded-xl shadow-xl p-8 border border-gray-700">
              <h2 className="text-2xl font-bold text-white mb-6">
                Send us a Message
              </h2>

              {/* Status Messages */}
              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-900 bg-opacity-30 border border-green-600 rounded-lg flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
                  <p className="text-green-300">
                    Thank you! Your message has been sent successfully. We'll get back to you soon.
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-900 bg-opacity-30 border border-red-600 rounded-lg flex items-center space-x-3">
                  <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
                  <p className="text-red-300">
                    Sorry, there was an error sending your message. Please try again or email us directly.
                  </p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name Field */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-200 mb-2">
                    <User className="inline h-4 w-4 mr-2" />
                    Your Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 bg-[#273349] border rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-[#ff7e00] focus:border-[#ff7e00] transition-colors ${
                      validationErrors.name ? 'border-red-500' : 'border-gray-600'
                    }`}
                    placeholder="Enter your full name"
                  />
                  {validationErrors.name && (
                    <p className="mt-1 text-sm text-red-400">{validationErrors.name}</p>
                  )}
                </div>

                {/* Email Field */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-200 mb-2">
                    <Mail className="inline h-4 w-4 mr-2" />
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 bg-[#273349] border rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-[#ff7e00] focus:border-[#ff7e00] transition-colors ${
                      validationErrors.email ? 'border-red-500' : 'border-gray-600'
                    }`}
                    placeholder="Enter your email address"
                  />
                  {validationErrors.email && (
                    <p className="mt-1 text-sm text-red-400">{validationErrors.email}</p>
                  )}
                </div>

                {/* Message Field */}
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-200 mb-2">
                    <MessageSquare className="inline h-4 w-4 mr-2" />
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={6}
                    value={formData.message}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 bg-[#273349] border rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-[#ff7e00] focus:border-[#ff7e00] transition-colors resize-vertical ${
                      validationErrors.message ? 'border-red-500' : 'border-gray-600'
                    }`}
                    placeholder="Please describe your issue or question in detail..."
                  />
                  {validationErrors.message && (
                    <p className="mt-1 text-sm text-red-400">{validationErrors.message}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-400">
                    {formData.message.length}/500 characters
                  </p>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-[#ff7e00] to-[#e11d48] text-white py-3 px-6 rounded-lg font-semibold hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center space-x-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <Send className="h-5 w-5" />
                      <span>Send Message</span>
                    </>
                  )}
                </button>

                <p className="text-xs text-gray-400 text-center">
                  This form is protected by reCAPTCHA and our privacy policy applies.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;